import React, { useState, useEffect } from 'react';
import { Moon, Sun, Trash2, Info, LogOut, Shield, ChevronDown, FileSpreadsheet, FileText, Loader2, Check, AlertCircle, Building2, Sparkles, FileUp, Crown, Zap, Star, ArrowRight, Package, Plane, Users, Camera } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBudget } from '@/contexts/BudgetContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { BudgetGoalsSettings } from './BudgetGoalsSettings';
import { ScanPackModal } from '@/components/subscription/ScanPackModal';
import { TransactionPackModal } from '@/components/subscription/TransactionPackModal';
import { PowerPackModal } from '@/components/subscription/PowerPackModal';
import { toast } from 'sonner';

const CATEGORIES = [
  "Food & Dining", "Transportation", "Utilities", "Entertainment",
  "Shopping", "Healthcare", "Education", "Savings", "Other",
  "Salary", "Freelance", "Investment", "Gift", "Refund"
];

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
];

function CurrencyButton({ currency, isSelected, onClick }) {
  return (
    <button onClick={onClick} className={`w-full flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors border-b border-border/30 last:border-0 ${isSelected ? 'bg-primary/5' : ''}`}>
      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
        <span className="text-sm font-bold">{currency.symbol}</span>
      </div>
      <div className="flex-1 text-left">
        <p className="text-sm font-medium">{currency.code}</p>
        <p className="text-xs text-muted-foreground">{currency.name}</p>
      </div>
      {isSelected && <div className="w-2 h-2 rounded-full bg-primary" />}
    </button>
  );
}

function ProjectButton({ project, isSelected, onClick }) {
  return (
    <button onClick={onClick} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted/50 hover:bg-muted'}`}>
      {project ? project.name : 'No Project'}
    </button>
  );
}

export const SettingsPanel = ({ onOpenPricing }) => {
  const { settings, toggleDarkMode, reloadData, updateSettings } = useBudget();
  const { user, logout, authFetch } = useAuth();
  const subscriptionCtx = useSubscription();
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false);
  const [showScanPackModal, setShowScanPackModal] = useState(false);
  const [showTransactionPackModal, setShowTransactionPackModal] = useState(false);
  const [showPowerPackModal, setShowPowerPackModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  
  // Bank statement import state
  const [showBankImportDialog, setShowBankImportDialog] = useState(false);
  const [bankPreview, setBankPreview] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankImporting, setBankImporting] = useState(false);
  const [editingTxIndex, setEditingTxIndex] = useState(null);
  
  // PDF import state
  const [showPdfImportDialog, setShowPdfImportDialog] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfImporting, setPdfImporting] = useState(false);
  const [pdfEditingIdx, setPdfEditingIdx] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await authFetch('/api/projects');
        if (response.ok) {
          const data = await response.json();
          setProjects(data || []);
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    };
    fetchProjects();
  }, [authFetch]);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await authFetch('/api/subscription/status');
        if (response.ok) {
          const data = await response.json();
          setSubscription(data);
        } else if (response.status === 401) {
          // Don't trigger logout, just skip subscription display
          console.log('Subscription fetch: auth required, skipping');
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      } finally {
        setSubLoading(false);
      }
    };
    // Only fetch if user is logged in
    if (user) {
      fetchSubscription();
    } else {
      setSubLoading(false);
    }
  }, [authFetch, user]);

  // Listen for header import events
  useEffect(() => {
    const handleBankImport = () => { setBankPreview(null); setSelectedProject(null); setShowBankImportDialog(true); };
    const handlePdfImport = () => { setPdfPreview(null); setSelectedProject(null); setShowPdfImportDialog(true); };
    window.addEventListener('openBankImport', handleBankImport);
    window.addEventListener('openPdfImport', handlePdfImport);
    return () => {
      window.removeEventListener('openBankImport', handleBankImport);
      window.removeEventListener('openPdfImport', handlePdfImport);
    };
  }, []);

  const currentCurrency = CURRENCIES.find(c => c.code === settings.currency) || CURRENCIES[0];

  const handleCurrencyChange = async (currencyCode) => {
    await updateSettings({ currency: currencyCode });
    setShowCurrencyPicker(false);
    toast.success(`Currency changed to ${currencyCode}`);
  };

  // Bank statement handlers
  const handleBankStatementUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBankLoading(true);
    setBankPreview(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await authFetch('/api/import/bank-statement/preview', { method: 'POST', body: formData, headers: {} });
      const result = await response.json();
      if (response.ok) setBankPreview(result);
      else toast.error(result.detail || 'Failed to parse statement');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setBankLoading(false);
    }
  };

  const handleBankCategoryChange = (index, newCategory) => {
    if (!bankPreview) return;
    const updated = { ...bankPreview };
    updated.transactions[index].category = newCategory;
    setBankPreview(updated);
    setEditingTxIndex(null);
  };

  const handleConfirmBankImport = async () => {
    if (!bankPreview) return;
    setBankImporting(true);
    try {
      const response = await authFetch('/api/import/bank-statement/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactions: bankPreview.transactions, project_id: selectedProject })
      });
      const result = await response.json();
      if (response.ok) {
        toast.success(`Imported ${result.imported} transactions!`);
        await reloadData();
        setShowBankImportDialog(false);
        setBankPreview(null);
      } else toast.error(result.detail || 'Import failed');
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setBankImporting(false);
    }
  };

  // PDF Import handlers
  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfLoading(true);
    setPdfPreview(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await authFetch('/api/import/pdf/preview', { method: 'POST', body: formData, headers: {} });
      const result = await response.json();
      if (response.ok) setPdfPreview(result);
      else toast.error(result.detail || 'Failed to parse PDF');
    } catch (error) {
      toast.error('Failed to upload PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePdfCategoryChange = (index, newCategory) => {
    if (!pdfPreview) return;
    const updated = { ...pdfPreview };
    updated.transactions[index].category = newCategory;
    setPdfPreview(updated);
    setPdfEditingIdx(null);
  };

  const handleConfirmPdfImport = async () => {
    if (!pdfPreview) return;
    setPdfImporting(true);
    try {
      const response = await authFetch('/api/import/pdf/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactions: pdfPreview.transactions, project_id: selectedProject })
      });
      const result = await response.json();
      if (response.ok) {
        toast.success(`Imported ${result.imported} transactions!`);
        await reloadData();
        setShowPdfImportDialog(false);
        setPdfPreview(null);
      } else toast.error(result.detail || 'Import failed');
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setPdfImporting(false);
    }
  };

  const handleClearData = async () => {
    try {
      // Use bulk delete endpoint
      const response = await authFetch('/api/transactions', { method: 'DELETE' });
      if (response.ok) {
        const result = await response.json();
        await reloadData();
        toast.success(`All data cleared! (${result.count} transactions deleted)`);
      } else {
        throw new Error('Failed to clear data');
      }
    } catch (error) {
      console.error('Clear data error:', error);
      toast.error('Failed to clear data');
    }
  };

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U';

  // Build currency buttons
  const currencyButtons = [];
  for (let i = 0; i < CURRENCIES.length; i++) {
    const c = CURRENCIES[i];
    currencyButtons.push(<CurrencyButton key={c.code} currency={c} isSelected={c.code === settings.currency} onClick={() => handleCurrencyChange(c.code)} />);
  }

  // Build project buttons
  const projectButtons = [<ProjectButton key="none" project={null} isSelected={!selectedProject} onClick={() => setSelectedProject(null)} />];
  for (let i = 0; i < projects.length; i++) {
    const p = projects[i];
    projectButtons.push(<ProjectButton key={p.id} project={p} isSelected={selectedProject === p.id} onClick={() => setSelectedProject(p.id)} />);
  }

  return (
    <div className="space-y-4">
      {/* Account */}
      <Card className="border border-border/50 shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Account</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 py-2">
            <Avatar className="h-14 w-14">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-primary/10 text-primary text-lg">{getInitials(user?.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground truncate">{user?.name}</p>
              <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Separator className="my-3" />
          {user?.is_admin && (
            <Button variant="outline" className="w-full justify-start gap-3 mb-2" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'admin' }))} data-testid="settings-admin-btn">
              <Shield className="w-4 h-4" /> Admin Dashboard
            </Button>
          )}
          <Button variant="outline" className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={logout} data-testid="settings-logout-btn">
            <LogOut className="w-4 h-4" /> Sign Out
          </Button>
        </CardContent>
      </Card>

      {/* Subscription Plan - Redesigned for better UX */}
      <Card className={`border shadow-card overflow-hidden ${
        subscription?.tier === 'elite' 
          ? 'border-amber-500/50 bg-gradient-to-br from-amber-500/5 to-orange-500/5' 
          : subscription?.tier === 'prime'
          ? 'border-violet-500/50 bg-gradient-to-br from-violet-500/5 to-fuchsia-500/5'
          : 'border-border/50'
      }`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                subscription?.tier === 'elite' 
                  ? 'bg-gradient-to-br from-amber-500 to-orange-500' 
                  : subscription?.tier === 'prime'
                  ? 'bg-gradient-to-br from-violet-500 to-fuchsia-500'
                  : 'bg-muted'
              }`}>
                {subscription?.tier === 'elite' ? (
                  <Crown className="w-5 h-5 text-white" />
                ) : subscription?.tier === 'prime' ? (
                  <Star className="w-5 h-5 text-white" />
                ) : (
                  <Zap className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <div>
                <CardTitle className="text-base font-semibold">
                  {subscription?.tier === 'elite' ? 'Elite Plan' : 
                   subscription?.tier === 'prime' ? 'Prime Plan' : 'Free Plan'}
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  {subscription?.tier === 'elite' ? 'Unlimited everything!' : 
                   subscription?.tier === 'prime' ? '$4.99/month' : 'Basic features'}
                </p>
              </div>
            </div>
            {subscription?.is_trial && (
              <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                Trial Active
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {subLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : subscription ? (
            <div className="space-y-4">
              {/* Usage Overview - Visual Cards */}
              <div className="grid grid-cols-2 gap-3">
                {/* AI Scans Card */}
                <div className={`p-3 rounded-xl border ${
                  subscription.scans_limit !== 999999 && 
                  subscription.scans_used >= (subscription.total_scans_available || subscription.scans_limit)
                    ? 'border-red-500/30 bg-red-500/5'
                    : 'border-border/50 bg-muted/30'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Camera className="w-4 h-4 text-emerald-500" />
                    <span className="text-xs font-medium">AI Scans</span>
                  </div>
                  <p className="text-xl font-bold">
                    {subscription.scans_limit === 999999 
                      ? '∞' 
                      : `${subscription.scans_used}/${subscription.total_scans_available || subscription.scans_limit}`
                    }
                  </p>
                  {subscription.bonus_scans > 0 && (
                    <p className="text-[10px] text-emerald-600">+{subscription.bonus_scans} bonus</p>
                  )}
                  {subscription.scans_limit !== 999999 && (
                    <Progress 
                      value={(subscription.scans_used / (subscription.total_scans_available || subscription.scans_limit)) * 100} 
                      className="h-1.5 mt-2"
                    />
                  )}
                </div>

                {/* Transactions Card */}
                <div className={`p-3 rounded-xl border ${
                  subscription.budget_entries_limit !== 999999 && 
                  ((subscription.budget_entries_used || 0) >= (subscription.budget_entries_limit || 15))
                    ? 'border-red-500/30 bg-red-500/5'
                    : 'border-border/50 bg-muted/30'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-medium">Transactions</span>
                  </div>
                  <p className="text-xl font-bold">
                    {subscription.budget_entries_limit === 999999 
                      ? '∞' 
                      : `${(subscription.budget_entries_used || 0) + (subscription.split_entries_used || 0)}/${(subscription.budget_entries_limit || 15) + (subscription.split_entries_limit || 15)}`
                    }
                  </p>
                  {(subscription.bonus_transactions || 0) > 0 && (
                    <p className="text-[10px] text-blue-600">+{subscription.bonus_transactions} bonus</p>
                  )}
                  {subscription.budget_entries_limit !== 999999 && (
                    <Progress 
                      value={(((subscription.budget_entries_used || 0) + (subscription.split_entries_used || 0)) / 
                             ((subscription.budget_entries_limit || 15) + (subscription.split_entries_limit || 15))) * 100} 
                      className="h-1.5 mt-2 [&>div]:bg-blue-500"
                    />
                  )}
                </div>
              </div>

              {/* Detailed Breakdown - Collapsible */}
              {subscription.budget_entries_limit !== 999999 && (
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-4 py-1">
                  <span className="flex items-center gap-1">
                    <Plane className="w-3 h-3" /> Budget: {subscription.budget_entries_used || 0}/{subscription.budget_entries_limit || 15}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" /> Split: {subscription.split_entries_used || 0}/{subscription.split_entries_limit || 15}
                  </span>
                </div>
              )}

              {/* Warning Message */}
              {subscription.budget_entries_limit !== 999999 && 
                (subscription.scans_used >= (subscription.total_scans_available || subscription.scans_limit) ||
                 (subscription.budget_entries_used || 0) >= (subscription.budget_entries_limit || 15) ||
                 (subscription.split_entries_used || 0) >= (subscription.split_entries_limit || 15)) && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <p className="text-xs text-amber-700 dark:text-amber-400 font-medium flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    You've reached your limit! Buy more or upgrade for unlimited access.
                  </p>
                </div>
              )}

              {/* Quick Actions */}
              <div className="space-y-2">
                {/* Buy More Section */}
                {subscription.scans_limit !== 999999 && (
                  <div className="grid grid-cols-3 gap-2">
                    <Button 
                      variant="outline"
                      size="sm"
                      className="h-auto py-2.5 flex-col hover:border-emerald-500/50 hover:bg-emerald-500/5"
                      onClick={() => setShowScanPackModal(true)}
                      data-testid="buy-scans-btn"
                    >
                      <Camera className="w-4 h-4 mb-1 text-emerald-500" />
                      <span className="text-[10px] font-medium">Scans</span>
                      <span className="text-[9px] text-muted-foreground">$0.99+</span>
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      className="h-auto py-2.5 flex-col hover:border-blue-500/50 hover:bg-blue-500/5"
                      onClick={() => setShowTransactionPackModal(true)}
                      data-testid="buy-transactions-btn"
                    >
                      <Package className="w-4 h-4 mb-1 text-blue-500" />
                      <span className="text-[10px] font-medium">Transactions</span>
                      <span className="text-[9px] text-muted-foreground">$0.99+</span>
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      className="h-auto py-2.5 flex-col border-amber-500/50 bg-amber-500/5 hover:bg-amber-500/10"
                      onClick={() => setShowPowerPackModal(true)}
                      data-testid="buy-power-pack-btn"
                    >
                      <Sparkles className="w-4 h-4 mb-1 text-amber-500" />
                      <span className="text-[10px] font-medium">Power Pack</span>
                      <span className="text-[9px] text-amber-600">Save 25%</span>
                    </Button>
                  </div>
                )}

                {/* Upgrade Button */}
                {subscription.tier !== 'elite' && (
                  <Button 
                    className={`w-full ${
                      subscription.tier === 'essentials'
                        ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600'
                        : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
                    } shadow-lg`}
                    onClick={onOpenPricing}
                    data-testid="upgrade-plan-btn"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    {subscription.tier === 'essentials' ? 'Upgrade to Prime - $4.99/mo' : 'Upgrade to Elite - $9.99/mo'}
                  </Button>
                )}
              </div>

              {/* Reset Info */}
              {subscription.entries_reset_date && subscription.budget_entries_limit !== 999999 && (
                <p className="text-[10px] text-muted-foreground text-center pt-1">
                  Usage resets {(() => {
                    const resetDate = new Date(subscription.entries_reset_date);
                    resetDate.setMonth(resetDate.getMonth() + 1);
                    return resetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  })()}
                </p>
              )}

              {/* Elite Success Message */}
              {subscription.tier === 'elite' && (
                <div className="p-3 rounded-lg bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 text-center">
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                    🎉 You have the best plan! Enjoy unlimited everything.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Unable to load subscription status</p>
          )}
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card className="border border-border/50 shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Appearance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              {settings.darkMode ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-warning" />}
              <div>
                <Label className="text-sm font-medium">Dark Mode</Label>
                <p className="text-xs text-muted-foreground">Switch between light and dark theme</p>
              </div>
            </div>
            <Switch checked={settings.darkMode} onCheckedChange={toggleDarkMode} data-testid="dark-mode-switch" />
          </div>
        </CardContent>
      </Card>

      {/* Currency */}
      <Card className="border border-border/50 shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Currency</CardTitle>
        </CardHeader>
        <CardContent>
          <button onClick={() => setShowCurrencyPicker(!showCurrencyPicker)} className="w-full flex items-center justify-between py-3 px-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <span className="text-lg font-bold text-primary">{currentCurrency.symbol}</span>
              </div>
              <div className="text-left">
                <p className="font-medium">{currentCurrency.code}</p>
                <p className="text-xs text-muted-foreground">{currentCurrency.name}</p>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showCurrencyPicker ? 'rotate-180' : ''}`} />
          </button>
          {showCurrencyPicker && <div className="mt-3 max-h-64 overflow-y-auto rounded-xl border border-border bg-background">{currencyButtons}</div>}
        </CardContent>
      </Card>

      {/* Budget Goals */}
      <BudgetGoalsSettings />

      {/* Data Management */}
      <Card className="border border-border/50 shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Data Management</CardTitle>
          <CardDescription className="text-xs">Clear all transaction data</CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="w-full justify-start gap-3 h-12 border-destructive/50 text-destructive hover:bg-destructive/10" data-testid="clear-data-btn">
                <Trash2 className="w-5 h-5" />
                <div className="text-left">
                  <p className="text-sm font-medium">Clear All Data</p>
                  <p className="text-xs opacity-70">Delete all transactions</p>
                </div>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-[90vw] sm:max-w-[400px] rounded-xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Clear All Data?</AlertDialogTitle>
                <AlertDialogDescription>This will permanently delete all your transactions.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleClearData} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Clear All</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>

      {/* About */}
      <Card className="border border-border/50 shadow-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center">
              <Info className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">SnapTally</p>
              <p className="text-xs text-muted-foreground">Version 2.2.0 • Cloud Sync Enabled</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bank Statement Import Dialog */}
      <Dialog open={showBankImportDialog} onOpenChange={setShowBankImportDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-[600px] max-h-[90vh] rounded-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" /> Import Bank Statement
              <Badge className="text-[10px] bg-violet-500/10 text-violet-500 border-0"><Sparkles className="w-3 h-3 mr-1" />AI</Badge>
            </DialogTitle>
            <DialogDescription>Upload CSV/Excel and AI will auto-categorize</DialogDescription>
          </DialogHeader>
          
          {!bankPreview ? (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-border rounded-xl p-8 text-center relative">
                {bankLoading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 animate-spin text-violet-500" />
                    <p className="text-sm text-muted-foreground">Analyzing with AI...</p>
                  </div>
                ) : (
                  <>
                    <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm font-medium mb-1">Drop bank statement here</p>
                    <p className="text-xs text-muted-foreground mb-4">Chase, Bank of America, Wells Fargo, Capital One</p>
                    <input type="file" accept=".csv,.xlsx,.xls" onChange={handleBankStatementUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-violet-500/5 rounded-lg border border-violet-500/20">
                <div>
                  <p className="text-sm font-medium">{bankPreview.bank_detected}</p>
                  <p className="text-xs text-muted-foreground">{bankPreview.date_range?.start} to {bankPreview.date_range?.end}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold">{bankPreview.total_transactions}</p>
                  <p className="text-[10px] text-muted-foreground">transactions</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <p className="text-[10px] text-muted-foreground">Expenses</p>
                  <p className="text-sm font-bold text-red-500">${bankPreview.summary?.total_expenses?.toFixed(2)}</p>
                </div>
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <p className="text-[10px] text-muted-foreground">Income</p>
                  <p className="text-sm font-bold text-emerald-500">${bankPreview.summary?.total_income?.toFixed(2)}</p>
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium mb-2 block">Assign to Project</Label>
                <div className="flex flex-wrap gap-2">{projectButtons}</div>
              </div>
              <ScrollArea className="h-48 border rounded-lg">
                <div className="p-2 space-y-1">
                  {bankPreview.transactions.slice(0, 50).map((tx, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/30 text-xs">
                      <span className="w-20 text-muted-foreground">{tx.date}</span>
                      <span className="flex-1 truncate">{tx.description}</span>
                      <button onClick={() => setEditingTxIndex(editingTxIndex === idx ? null : idx)} className={`px-2 py-0.5 rounded text-[10px] font-medium ${tx.type === 'income' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-blue-500/10 text-blue-600'}`}>{tx.category}</button>
                      <span className={`w-16 text-right font-medium ${tx.type === 'income' ? 'text-emerald-500' : 'text-red-500'}`}>{tx.type === 'income' ? '+' : '-'}${tx.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {editingTxIndex !== null && (
                <div className="p-2 bg-muted/30 rounded-lg">
                  <p className="text-xs mb-2">Change category:</p>
                  <div className="flex flex-wrap gap-1">
                    {CATEGORIES.map(cat => (
                      <button key={cat} onClick={() => handleBankCategoryChange(editingTxIndex, cat)} className={`px-2 py-1 rounded text-[10px] ${bankPreview.transactions[editingTxIndex]?.category === cat ? 'bg-primary text-primary-foreground' : 'bg-background border hover:bg-muted'}`}>{cat}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setBankPreview(null)}>Back</Button>
                <Button className="flex-1 bg-violet-500 hover:bg-violet-600" onClick={handleConfirmBankImport} disabled={bankImporting}>
                  {bankImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Import {bankPreview.total_transactions}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Import Dialog */}
      <Dialog open={showPdfImportDialog} onOpenChange={setShowPdfImportDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-[600px] max-h-[90vh] rounded-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5" /> Import from PDF
              <Badge className="text-[10px] bg-red-500/10 text-red-500 border-0"><Sparkles className="w-3 h-3 mr-1" />AI</Badge>
            </DialogTitle>
            <DialogDescription>Upload PDF and AI will extract transactions</DialogDescription>
          </DialogHeader>
          
          {!pdfPreview ? (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-border rounded-xl p-8 text-center relative">
                {pdfLoading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 animate-spin text-red-500" />
                    <p className="text-sm text-muted-foreground">AI is reading your PDF...</p>
                  </div>
                ) : (
                  <>
                    <FileText className="w-12 h-12 mx-auto text-red-400 mb-3" />
                    <p className="text-sm font-medium mb-1">Drop PDF here</p>
                    <p className="text-xs text-muted-foreground mb-4">Bank statements, financial documents</p>
                    <input type="file" accept=".pdf" onChange={handlePdfUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-red-500/5 rounded-lg border border-red-500/20">
                <div>
                  <p className="text-sm font-medium">{pdfPreview.filename}</p>
                  <p className="text-xs text-muted-foreground">{pdfPreview.date_range?.start} to {pdfPreview.date_range?.end}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold">{pdfPreview.total_transactions}</p>
                  <p className="text-[10px] text-muted-foreground">transactions</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <p className="text-[10px] text-muted-foreground">Expenses</p>
                  <p className="text-sm font-bold text-red-500">${pdfPreview.summary?.total_expenses?.toFixed(2)}</p>
                </div>
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <p className="text-[10px] text-muted-foreground">Income</p>
                  <p className="text-sm font-bold text-emerald-500">${pdfPreview.summary?.total_income?.toFixed(2)}</p>
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium mb-2 block">Assign to Project</Label>
                <div className="flex flex-wrap gap-2">{projectButtons}</div>
              </div>
              <ScrollArea className="h-48 border rounded-lg">
                <div className="p-2 space-y-1">
                  {pdfPreview.transactions.slice(0, 50).map((tx, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/30 text-xs">
                      <span className="w-20 text-muted-foreground">{tx.date}</span>
                      <span className="flex-1 truncate">{tx.description}</span>
                      <button onClick={() => setPdfEditingIdx(pdfEditingIdx === idx ? null : idx)} className={`px-2 py-0.5 rounded text-[10px] font-medium ${tx.type === 'income' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-blue-500/10 text-blue-600'}`}>{tx.category}</button>
                      <span className={`w-16 text-right font-medium ${tx.type === 'income' ? 'text-emerald-500' : 'text-red-500'}`}>{tx.type === 'income' ? '+' : '-'}${tx.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {pdfEditingIdx !== null && (
                <div className="p-2 bg-muted/30 rounded-lg">
                  <p className="text-xs mb-2">Change category:</p>
                  <div className="flex flex-wrap gap-1">
                    {CATEGORIES.map(cat => (
                      <button key={cat} onClick={() => handlePdfCategoryChange(pdfEditingIdx, cat)} className={`px-2 py-1 rounded text-[10px] ${pdfPreview.transactions[pdfEditingIdx]?.category === cat ? 'bg-primary text-primary-foreground' : 'bg-background border hover:bg-muted'}`}>{cat}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setPdfPreview(null)}>Back</Button>
                <Button className="flex-1 bg-red-500 hover:bg-red-600" onClick={handleConfirmPdfImport} disabled={pdfImporting}>
                  {pdfImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Import {pdfPreview.total_transactions}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Scan Pack Modal */}
      <ScanPackModal 
        open={showScanPackModal} 
        onOpenChange={setShowScanPackModal} 
      />
      
      {/* Transaction Pack Modal */}
      <TransactionPackModal 
        open={showTransactionPackModal} 
        onOpenChange={setShowTransactionPackModal} 
      />
      
      {/* Power Pack Modal */}
      <PowerPackModal 
        open={showPowerPackModal} 
        onOpenChange={setShowPowerPackModal} 
      />
    </div>
  );
};
